/**
 * A component that can be used to specify a core set of extra attributes that
 * should be included in conversions marked by the children of this component.
 * These core extra attributes are defined by the learningTimeInfo prop of this component.
 * The extra attributes specified here will be merged with extra attributes
 * specified by all parent ConversionContextProvider components.
 *
 * Additionally, this component can be used to expose a markConversion function
 * that can be used by its children to log conversions with the extra attributes
 * defined across the component tree. This requires using the function-as-child
 * pattern.
 *
 * For example, in the following snippet:
 *
 *  const OuterComponent = () => {
 *      <ConversionContextProvider
 *         learningTimeInfo={highLevelLearningTimeInfo}
 *      >
 *         <ConversionContextProvider
 *              learningTimeInfo={lowLevelLearningTimeInfo}
 *          >
 *              <InnerComponent />
 *          </ConversionContextProvider>
 *      </ConversionContextProvider>
 *  }
 *
 * const InnerComponent = () => {
 *    const markConversion = useMarkConversion();
 *    const handleClick = useCallback(() => markConversion(), [markConversion]);
 *
 *    return <button onClick={handleClick}>Click me!</button>;
 * }
 *
 * The inner component can use the `markConversion` function to log conversions.
 * It will mark these conversions with both highLevelLearningTimeInfo and
 * lowLevelLearningTimeInfo merged together.
 *
 * Notes:
 *
 * Usage of the withConversionContext HOC is preferable to using the
 * ConversionContextProvider component directly. ConversionContextProvider should only
 * be used when the HOC is impossible to work with, e.g.:
 *
 * - A component is wrapped in multiple HOCs, and the types are too complex to
 *   specify
 * - A component needs to take a ref, or you need access to its methods or
 *   properties (e.g. exercise-modal.jsx, exercise-chrome.jsx)
 *
 * Duplicate keys in the lowLevelLearningTimeInfo will overwrite the same
 * key-value pairs in highLevelLearningTimeInfo. The idea is, we are likely to get updated
 * and more relevant learningTimeInfo lower down in the component tree, so they should
 * overwrite any keys that were defined in the ancestors.
 */
import * as React from "react";
import arePropsEqual from "react-fast-compare";

import {LearningTimeInfoApiContext} from "./internal/learning-time-info-api-context.ts";
import {LearningTimeInfoApi} from "./internal/learning-time-info-api.ts";
// NOTE(slim): OK to import BigBingoInternals from infrastructure file.
import {
    type Conversion,
    type MarkConversionT,
    type PartialLearningTimeInfo,
    type PublishAnalyticsEventT,
} from "./types.ts";

// Re-export types so that <ConversionContextProvider> consumers don't need to import
// from this file as well as shared-package/learning-time/types.js
export type {
    Conversion,
    PartialLearningTimeInfo,
    MarkConversionT,
    PublishAnalyticsEventT,
};

interface ConversionContextProps {
    learningTimeInfo: PartialLearningTimeInfo;
    children?: React.ReactNode;
}

const ConversionContextProvider = (props: ConversionContextProps) => {
    const {children, learningTimeInfo} = props;
    const prevLtiApi = React.useContext(LearningTimeInfoApiContext);
    const learningTimeInfoRef = React.useRef(learningTimeInfo);

    const ltiApi = React.useMemo(
        () => new LearningTimeInfoApi(learningTimeInfo, prevLtiApi),
        // NOTE(kevinb): Changes to the learningTimeInfo prop are handled
        // by the `useEffect` below which calls `update` on the ltiApi to
        // update the value within the LearningTimeInfoApi instance without
        // triggering a rerender.  Since this data is only used by
        // `useMarkConversion` and `usePublishAnalyticsEvent` we don't need
        // to trigger re-renders as the functions those hooks return can
        // grab the current value from the LearningTimeInfoApi instance in
        // the nearest LearningTimeInfoContext.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
        [],
    );

    // We use a ref to track the learningTimeInfo prop so that we can
    // avoid delays caused by the use of `useEffect`.
    if (learningTimeInfoRef.current !== learningTimeInfo) {
        ltiApi.update(learningTimeInfo);
        learningTimeInfoRef.current = learningTimeInfo;
    }

    return (
        <LearningTimeInfoApiContext.Provider value={ltiApi}>
            {children}
        </LearningTimeInfoApiContext.Provider>
    );
};

const MemoizedConversionContextProvider = React.memo(
    ConversionContextProvider,
    arePropsEqual,
);

export {MemoizedConversionContextProvider as ConversionContextProvider};
